import * as Raven from 'raven-js';
import * as cmp from 'semver-compare';

import { environment } from './environments/environment';
import { browser } from './browser';


/**
 * Detects the browser version and returns true if the browser is supported, false if we know for
 * sure it is unsupported, or null if browser detection fails or the browser is not one we recognize.
 * @return {boolean|null}
 */
export function isBrowserSupported() {
  // Ignore CloudFlare-AlwaysOnline browser
  if (/^.*CloudFlare-AlwaysOnline.*$/.test(window.navigator.userAgent)) {
    return false;
  }

  // If browser detection fails, return null (can' say for sure if supported)
  if (!browser) {
    return null;
  }

  // If browser detection succeeds, ensure minimum version
  switch (browser.name) {
    case 'chrome':
      return cmp(browser.version, '60') >= 0;
    case 'firefox':
      return cmp(browser.version, '40') >= 0;
    case 'ie':
      return cmp(browser.version, '10') >= 0;
    case 'opera':
      return cmp(browser.version, '48') >= 0;
    case 'ios':
    case 'safari':
      return cmp(browser.version, '9') >= 0;
    case 'phantomjs':
      return false;
    default:
      // Can't say for sure about other browsers - return null
      return null;
  }
}


/**
 * Initializes application-wide error logging to Sentry for all uncaught errors at the global level.
 */
export function initLogging(): void {
  Raven
    .config(environment.ravenDsn, {
      environment: environment.name,
      release: `v${environment.version}-${environment.name}`,
      stacktrace: true,
      // Ignore list based on: https://gist.github.com/impressiver/5092952
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        'Can\'t find variable: ZiteReader',
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // You can delete this if using raven.js > 1.0, which ignores these automatically.
        'Script error.'
      ],
      ignoreUrls: [
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i,  // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
      ],
      // Avoid logging errors for browsers we know we don't support. (If browser detection fails, isBrowserSupported()
      // returns null, in which case we should still log the error - hence the strict comparison to false.)
      shouldSendCallback: data => (isBrowserSupported() !== false)
    })
    .install();
}
